import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { AuthLayout } from '../components/auth/AuthLayout';
import { RequireAnonymous, RequireAuth } from '../components/auth/RequireAuth';
import { RequireOnboarded } from '../components/auth/RequireOnboarded';
import { StartPage } from '../pages/StartPage';
import { DynamicPrepPage } from '../pages/persona/DynamicPrepPage';
import { HomePage } from '../pages/HomePage';
import { CreateVideoPage } from '../pages/CreateVideoPage';
import { SystemPage } from '../pages/SystemPage';
import { SettingsPage } from '../pages/SettingsPage';
import { BalancePage } from '../pages/BalancePage';
import { MediaPage } from '../pages/MediaPage';
import { LooksPage } from '../pages/LooksPage';
import { StylePage } from '../pages/StylePage';
import { ProjectsPage } from '../pages/projects/ProjectsPage';
import { ProjectPage } from '../pages/projects/ProjectPage';
import { PersonaPage } from '../pages/persona/PersonaPage';
import { CapturePage } from '../pages/persona/CapturePage';
import { ConsentPage } from '../pages/persona/ConsentPage';
import { VoiceImprovePage } from '../pages/persona/VoiceImprovePage';
import { LegalPage } from '../pages/LegalPage';
import { ReferralPage } from '../pages/ReferralPage';
import { ReferralLandingPage } from '../pages/ReferralLandingPage';
import { AdminGuard } from '../components/admin/AdminGuard';
import { AdminLayout } from '../components/admin/AdminLayout';
import { AdminOverviewPage } from '../pages/admin/AdminOverviewPage';
import { AdminUsersPage } from '../pages/admin/AdminUsersPage';
import { AdminUserPage } from '../pages/admin/AdminUserPage';
import { AdminProjectsPage } from '../pages/admin/AdminProjectsPage';
import { AdminJobsPage } from '../pages/admin/AdminJobsPage';
import { AdminJobPage } from '../pages/admin/AdminJobPage';
import { AdminUsagePage } from '../pages/admin/AdminUsagePage';
import { AdminProvidersPage } from '../pages/admin/AdminProvidersPage';
import { AdminPricesPage } from '../pages/admin/AdminPricesPage';
import { AdminReferralsPage } from '../pages/admin/AdminReferralsPage';
import { AdminAuditPage } from '../pages/admin/AdminAuditPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { LoginPage } from '../pages/auth/LoginPage';
import { RegisterPage } from '../pages/auth/RegisterPage';
import { VerifyEmailPage } from '../pages/auth/VerifyEmailPage';
import { ForgotPasswordPage } from '../pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/auth/ResetPasswordPage';

/**
 * Роуты §78. Заглушки заменяются по мере прохождения фаз (docs/plan.md).
 * Запись персоны (§71) получит отдельный immersive-роут вне AppLayout в фазе 5.
 */
export const router = createBrowserRouter([
  // Публичные тексты документов — доступны и до регистрации (§4).
  { path: '/legal/:type', element: <LegalPage /> },

  // §40: короткая ссылка приглашения. Запоминает код и ведёт на регистрацию.
  { path: '/r/:code', element: <ReferralLandingPage /> },

  // §51: админка. Своя оболочка — визуальный язык здесь другой.
  {
    element: <AdminGuard />,
    children: [
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminOverviewPage /> },
          { path: 'users', element: <AdminUsersPage /> },
          { path: 'users/:id', element: <AdminUserPage /> },
          { path: 'projects', element: <AdminProjectsPage /> },
          { path: 'jobs', element: <AdminJobsPage /> },
          { path: 'jobs/:id', element: <AdminJobPage /> },
          { path: 'providers', element: <AdminProvidersPage /> },
          { path: 'usage', element: <AdminUsagePage /> },
          { path: 'prices', element: <AdminPricesPage /> },
          { path: 'referrals', element: <AdminReferralsPage /> },
          { path: 'audit', element: <AdminAuditPage /> },
        ],
      },
    ],
  },

  // Вход и регистрация — вне общего шелла.
  {
    element: <RequireAnonymous />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/register', element: <RegisterPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
          { path: '/reset-password', element: <ResetPasswordPage /> },
        ],
      },
    ],
  },

  // Подтверждение почты доступно и вошедшему, и пришедшему по ссылке из письма.
  {
    element: <AuthLayout />,
    children: [{ path: '/verify-email', element: <VerifyEmailPage /> }],
  },

  {
    element: <RequireAuth />,
    children: [
      // §6, §71: коридор первого входа — до первого ролика человек видит
      // только его, а не кабинет с восемью разделами.
      { path: '/start', element: <StartPage /> },
      // §71: запись персоны — отдельный immersive-экран вне общего шелла.
      { path: '/persona/capture', element: <CapturePage /> },
      // §9: подтверждение личности — тоже отдельный экран с камерой.
      { path: '/persona/consent', element: <ConsentPage /> },
      // §10.1: дополнительная запись голоса.
      { path: '/persona/voice', element: <VoiceImprovePage /> },
      // §22: подготовка к динамичным сценам — две короткие записи.
      { path: '/persona/dynamic', element: <DynamicPrepPage /> },
    ],
  },

  {
    path: '/',
    element: <RequireAuth />,
    children: [
      {
        // Кабинет открывается после первого ролика: до него ведёт коридор.
        element: <RequireOnboarded />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <HomePage /> },
              { path: 'create', element: <CreateVideoPage /> },
              { path: 'projects', element: <ProjectsPage /> },
              { path: 'projects/:id', element: <ProjectPage /> },
              { path: 'looks', element: <LooksPage /> },
              { path: 'style', element: <StylePage /> },
              { path: 'media', element: <MediaPage /> },
              { path: 'persona', element: <PersonaPage /> },
              { path: 'referral', element: <ReferralPage /> },
              { path: 'balance', element: <BalancePage /> },
              { path: 'settings', element: <SettingsPage /> },
              { path: 'system', element: <SystemPage /> },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
